/**
 * App store: React context over IndexedDB with the sync engine wired in.
 * Local-first: every action writes locally first, then queues for the
 * paired device. Remote payloads merge last-writer-wins by updatedAt.
 */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getAllEvents,
  getEvent,
  kvDelete,
  kvGet,
  kvSet,
  outboxClear,
  putEvents,
  wipeDb,
} from './db';
import { derivePairId } from './crypto';
import type { BackupFile } from './backup';
import { DEFAULT_SYNC_URL, registerPair, SyncEngine, verifyPair, type SyncStatus } from './sync';
import { nowIso } from './time';
import {
  DEFAULT_SETTINGS,
  type DeviceInfo,
  type PairingState,
  type Profile,
  type Role,
  type Settings,
  type SyncPayload,
  type TrackedEvent,
} from './types';

export interface AppState {
  ready: boolean;
  device: DeviceInfo | null;
  settings: Settings;
  profile: Profile | null;
  /** all non-deleted events, newest first */
  events: TrackedEvent[];
  pairing: PairingState | null;
  syncStatus: SyncStatus;
}

export interface ImportResult {
  added: number;
  updated: number;
  /** already present and not older, so left alone */
  skipped: number;
  profileRestored: boolean;
}

export interface AppActions {
  /** Partner devices omit the profile; it arrives via sync so mom's copy wins. */
  completeOnboarding: (role: Role, profile?: Profile) => Promise<void>;
  setSettings: (patch: Partial<Settings>) => Promise<void>;
  updateProfile: (patch: Partial<Omit<Profile, 'updatedAt'>>) => Promise<void>;
  /** Adds a fully-formed event (id, at, deviceId, role, updatedAt set by caller helpers below). */
  saveEvent: (event: TrackedEvent) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  /** Mom's device: create a pair and return the shareable code. */
  createPair: (serverUrl?: string) => Promise<string>;
  /** Partner device: join with the code from mom's device. */
  joinPair: (code: string, serverUrl?: string) => Promise<void>;
  unpair: () => Promise<void>;
  syncNow: () => Promise<void>;
  /** Merge a backup file into this device (last write wins). */
  importBackup: (backup: BackupFile) => Promise<ImportResult>;
  eraseAllData: () => Promise<void>;
}

const StateCtx = createContext<AppState | null>(null);
const ActionsCtx = createContext<AppActions | null>(null);

/**
 * Timestamps are local-time ISO strings WITH offsets, so they must be
 * compared as instants; lexicographic order breaks the moment the two
 * devices sit in different UTC offsets.
 */
function ts(iso: string): number {
  return Date.parse(iso);
}

function sortEvents(events: TrackedEvent[]): TrackedEvent[] {
  return events
    .filter((e) => !e.deleted)
    .sort((a, b) => ts(b.at) - ts(a.at));
}

export function newEventBase(device: DeviceInfo) {
  return {
    id: crypto.randomUUID(),
    at: nowIso(),
    deviceId: device.deviceId,
    role: device.role,
    updatedAt: nowIso(),
  };
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [settings, setSettingsState] = useState<Settings>(DEFAULT_SETTINGS);
  const [profile, setProfileState] = useState<Profile | null>(null);
  const [events, setEvents] = useState<TrackedEvent[]>([]);
  const [pairing, setPairing] = useState<PairingState | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('unpaired');

  const profileRef = useRef<Profile | null>(null);
  profileRef.current = profile;
  const deviceRef = useRef<DeviceInfo | null>(null);
  deviceRef.current = device;
  const pairingRef = useRef<PairingState | null>(null);
  pairingRef.current = pairing;
  /** Set after queueSnapshot exists; lets the engine recover from a relay-side wipe. */
  const pairLostRef = useRef<(() => Promise<void>) | null>(null);

  const applyRemote = useCallback(async (payloads: SyncPayload[]) => {
    const toPut: TrackedEvent[] = [];
    let remoteProfile: Profile | null = null;
    for (const p of payloads) {
      if (p.kind === 'event') {
        const existing = await getEvent(p.event.id);
        if (!existing || ts(existing.updatedAt) < ts(p.event.updatedAt)) toPut.push(p.event);
      } else if (p.kind === 'profile') {
        if (!remoteProfile || ts(remoteProfile.updatedAt) < ts(p.profile.updatedAt)) {
          remoteProfile = p.profile;
        }
      }
    }
    if (toPut.length > 0) {
      await putEvents(toPut);
      setEvents(sortEvents(await getAllEvents()));
    }
    if (remoteProfile) {
      const local = profileRef.current;
      if (!local || ts(local.updatedAt) < ts(remoteProfile.updatedAt)) {
        await kvSet('profile', remoteProfile);
        setProfileState(remoteProfile);
      }
    }
  }, []);

  const engineRef = useRef<SyncEngine | null>(null);
  if (!engineRef.current) {
    engineRef.current = new SyncEngine({
      onRemote: applyRemote,
      onStatus: setSyncStatus,
      onPairLost: async () => {
        await pairLostRef.current?.();
      },
    });
  }
  const engine = engineRef.current;

  // Boot: load everything from IndexedDB. Guarded so StrictMode's dev
  // double-invoke cannot run two boots concurrently and mint two device ids.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    void (async () => {
      let dev = await kvGet<DeviceInfo>('device');
      if (!dev) {
        dev = { deviceId: crypto.randomUUID(), role: 'mom', onboarded: false };
        await kvSet('device', dev);
      }
      const [st, prof, pair, evts] = await Promise.all([
        kvGet<Settings>('settings'),
        kvGet<Profile>('profile'),
        kvGet<PairingState>('pairing'),
        getAllEvents(),
      ]);
      setDevice(dev);
      if (st) setSettingsState({ ...DEFAULT_SETTINGS, ...st });
      if (prof) setProfileState(prof);
      if (pair) setPairing(pair);
      setEvents(sortEvents(evts));
      await engine.configure(pair ?? null, dev.deviceId);
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistDevice = useCallback(async (dev: DeviceInfo) => {
    await kvSet('device', dev);
    setDevice(dev);
  }, []);

  const setSettings = useCallback(
    async (patch: Partial<Settings>) => {
      const next = { ...settings, ...patch };
      await kvSet('settings', next);
      setSettingsState(next);
    },
    [settings],
  );

  const pushProfile = useCallback(
    async (next: Profile) => {
      await kvSet('profile', next);
      setProfileState(next);
      await engine.queue('profile', { kind: 'profile', profile: next });
    },
    [engine],
  );

  const updateProfile = useCallback(
    async (patch: Partial<Omit<Profile, 'updatedAt'>>) => {
      // Never fabricate a base profile on a device that has none: a partner
      // who edits before mom's profile arrives would author a sparse
      // prenatal profile that LWW-clobbers the real one on both phones.
      // The UI disables profile edits until a profile exists locally.
      const base = profileRef.current;
      if (!base) return;
      await pushProfile({ ...base, ...patch, updatedAt: nowIso() });
    },
    [pushProfile],
  );

  const completeOnboarding = useCallback(
    async (role: Role, prof?: Profile) => {
      const dev = deviceRef.current!;
      await persistDevice({ ...dev, role, onboarded: true });
      if (prof) await pushProfile(prof);
    },
    [persistDevice, pushProfile],
  );

  const saveEvent = useCallback(
    async (event: TrackedEvent) => {
      await putEvents([event]);
      setEvents(sortEvents(await getAllEvents()));
      await engine.queue(event.id, { kind: 'event', event });
    },
    [engine],
  );

  const deleteEvent = useCallback(
    async (id: string) => {
      const existing = await getEvent(id);
      if (!existing) return;
      const tombstone = { ...existing, deleted: true, updatedAt: nowIso() };
      await saveEvent(tombstone);
    },
    [saveEvent],
  );

  /**
   * Merge a backup file into this device. Same last-writer-wins rule the
   * sync path uses, so importing an older file can never overwrite newer
   * entries, and importing twice is a no-op.
   */
  const importBackup = useCallback(
    async (backup: BackupFile): Promise<ImportResult> => {
      const toPut: TrackedEvent[] = [];
      let updated = 0;
      for (const incoming of backup.events) {
        const existing = await getEvent(incoming.id);
        if (!existing) {
          toPut.push(incoming);
        } else if (ts(existing.updatedAt) < ts(incoming.updatedAt)) {
          toPut.push(incoming);
          updated += 1;
        }
      }
      if (toPut.length > 0) {
        await putEvents(toPut);
        setEvents(sortEvents(await getAllEvents()));
        for (const e of toPut) await engine.queue(e.id, { kind: 'event', event: e });
      }

      let profileRestored = false;
      const incomingProfile = backup.profile;
      if (incomingProfile) {
        const local = profileRef.current;
        if (!local || ts(local.updatedAt) < ts(incomingProfile.updatedAt)) {
          await pushProfile(incomingProfile);
          profileRestored = true;
        }
      }

      return {
        added: toPut.length - updated,
        updated,
        skipped: backup.events.length - toPut.length,
        profileRestored,
      };
    },
    [engine, pushProfile],
  );

  /** Queue the full local history so a newly paired device catches up. */
  const queueSnapshot = useCallback(async () => {
    const all = await getAllEvents();
    for (const e of all) await engine.queue(e.id, { kind: 'event', event: e });
    const prof = profileRef.current;
    if (prof) await engine.queue('profile', { kind: 'profile', profile: prof });
  }, [engine]);

  // Relay-side wipe recovery (retention alarm): the pairId is
  // self-authenticating, so re-registering and re-sending is safe.
  pairLostRef.current = async () => {
    const pair = pairingRef.current;
    if (!pair) return;
    await registerPair(pair.serverUrl, pair.pairId);
    await queueSnapshot();
  };

  const activatePairing = useCallback(
    async (code: string, serverUrl: string, isCreator: boolean) => {
      const pairId = await derivePairId(code);
      if (isCreator) {
        await registerPair(serverUrl, pairId);
      } else if (!(await verifyPair(serverUrl, pairId))) {
        // Registering here would silently create a brand-new empty pair on
        // any typo; a wrong code must fail loudly instead.
        throw new Error('unknown pair');
      }
      const state: PairingState = {
        code,
        pairId,
        cursor: 0,
        serverUrl,
        connectedAt: nowIso(),
      };
      await kvSet('pairing', state);
      setPairing(state);
      await engine.configure(state, deviceRef.current!.deviceId);
      // Only the inviting device seeds the pair with its history. A joining
      // device may hold another family's data (or the wrong pair on a
      // mistyped link) and must never upload its history automatically.
      if (isCreator) await queueSnapshot();
      await engine.syncNow();
    },
    [engine, queueSnapshot],
  );

  const createPair = useCallback(
    async (serverUrl: string = DEFAULT_SYNC_URL) => {
      const { generatePairCode } = await import('./crypto');
      const code = generatePairCode();
      await activatePairing(code, serverUrl, true);
      return code;
    },
    [activatePairing],
  );

  const joinPair = useCallback(
    async (code: string, serverUrl: string = DEFAULT_SYNC_URL) => {
      await activatePairing(code, serverUrl, false);
    },
    [activatePairing],
  );

  const unpair = useCallback(async () => {
    await kvDelete('pairing');
    // Drop queued payloads so this device's history can never be uploaded
    // into whatever pair is joined next.
    await outboxClear();
    setPairing(null);
    await engine.configure(null, deviceRef.current?.deviceId ?? '');
  }, [engine]);

  const syncNow = useCallback(() => engine.syncNow(), [engine]);

  const eraseAllData = useCallback(async () => {
    await engine.configure(null, '');
    await wipeDb();
    try {
      // Also drop localStorage flags (congrats dismissal, prenatal
      // checklist) so "erase all data" truly erases everything.
      localStorage.clear();
    } catch {
      // Storage failures must not block the wipe.
    }
    window.location.reload();
  }, [engine]);

  const state = useMemo<AppState>(
    () => ({ ready, device, settings, profile, events, pairing, syncStatus }),
    [ready, device, settings, profile, events, pairing, syncStatus],
  );
  const actions = useMemo<AppActions>(
    () => ({
      completeOnboarding,
      setSettings,
      updateProfile,
      saveEvent,
      deleteEvent,
      createPair,
      joinPair,
      unpair,
      syncNow,
      importBackup,
      eraseAllData,
    }),
    [
      completeOnboarding,
      setSettings,
      updateProfile,
      saveEvent,
      deleteEvent,
      createPair,
      joinPair,
      unpair,
      syncNow,
      importBackup,
      eraseAllData,
    ],
  );

  return (
    <StateCtx.Provider value={state}>
      <ActionsCtx.Provider value={actions}>{children}</ActionsCtx.Provider>
    </StateCtx.Provider>
  );
}

export function useApp(): AppState {
  const ctx = useContext(StateCtx);
  if (!ctx) throw new Error('useApp outside AppProvider');
  return ctx;
}

export function useActions(): AppActions {
  const ctx = useContext(ActionsCtx);
  if (!ctx) throw new Error('useActions outside AppProvider');
  return ctx;
}
